import React, { createContext, useEffect, useState } from 'react';
import axios from 'axios';
import { url } from '../../helpers/utils.js';
import { constants, routes } from '../../constants/index.js';
import { getUserData } from '../actions/user.js';
import _get from 'lodash/get.js';
import _forEach from 'lodash/forEach.js';
import { parseJSONWithFallback } from '../../helpers/utils.js'

const { LOCAL_STORAGE_KEYS, ATMOSPHERES } = constants;
const {
    SERVICE: { SSO }, 
    ENDPOINTS: { SSO: { VALIDATE_TOKEN } }
} = routes;

export const AppContext = createContext({});

const getToken = () => window.localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN);

const getUserProfile = () => {
	try {
		return JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_KEYS.PROFILE));
	} catch (e) {
	    return {}
	}
};

const validateToken = token => axios({
    method: 'POST',
    url: url.concat(SSO, VALIDATE_TOKEN),
    headers: { 'Content-Type': 'application/json' },
    data: JSON.stringify({ token })
});

const isTokenValid = async () => {
    const token = getToken();
    if (!token) {
        return false;
    }
    const tokenStatus = await validateToken(token);
    return !!_.get(tokenStatus, 'data.valid');
}

const AppContextProvider = ({appData, appSettings, query, children}) => {
    
    const locale = _get(appSettings, 'locale');
    const [ instagramAuthCode, setInstagramAuthCode] = useState(null);
    const [ instagramAuthError, setInstagramAuthError] = useState(false);
    const [ activeView, setActiveView ] = useState(null);
    const [ artCollection, setArtCollection ] = useState(appData.artCollection || []);
    const [ personalArtCollection, setPersonalArtCollection ] = useState([]);
    const [ cmsConfiguration, setCmsConfiguration ] = useState({labels:appData.labels, settings:appData.settings});
    const [ settings, setSettings ] = useState(appSettings);
    const [ canInitalRender, setCanInitalRender ] = useState(false); 
    const [ canRenderPersonalPage, setCanRenderPersonalPage ] = useState(false);
    const [ customerProfile, setCustomerProfile ] = useState(getUserProfile());
    const [ userToken, setUserToken ] = useState(getToken());
    const [ isUserLoggedIn, setIsUserLoggedIn ] = useState(null);
    const [ userPreferences, setUserPreferences ] = useState({});
    const [ webglConfigurations, setWebglConfigurations ] = useState([]);
    const [ selectedAtmosphere, setSelectedAtmosphere ] = useState(ATMOSPHERES.EMOTION);
    const [ showLoadingScreen, setShowLoadingScreen ] = useState(true);
    const [ allowGuestAccess, setAllowGuestAccess ] = useState(false);
    const [ guestExpiryTime, setGuestExpiryTime ] = useState(null);
    const [ quizResults, setQuizResults ] = useState({});

    const createPersonalArtCollection = (collection) => {
        const model = 'lbx';
        const language = _get(settings, 'language', 'en');
        const storageHost = _get(settings, 'campaignsStorageHost');
        const myCollection = collection.map((item)=> {
            const { id, atmosphere} = item;
            return {
                base: `${storageHost}/artwork/${model}/${atmosphere}/${id}.jpg`,
                data: {
                    atmosphere
                },
                type:'image',
                id 
            }
        });

        setPersonalArtCollection(myCollection);
    }

    useEffect( async () => {
        const isLoggedIn = await isTokenValid();
        setIsUserLoggedIn(isLoggedIn);
        setCanInitalRender(true);
    }, []);

    useEffect( async () => {
        if (!_.isEmpty(quizResults)) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.QUIZ, JSON.stringify(quizResults))
        }
    }, [quizResults]);

    useEffect( async () => {
        if (guestExpiryTime) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.GUEST, JSON.stringify(guestExpiryTime))
        }
    }, [guestExpiryTime]);

    useEffect( async () => {
        if (isUserLoggedIn) {
            await getUserData({
                setUserPreferences,
                setWebglConfigurations,
                locale,
                token:userToken
            });

            setCanRenderPersonalPage(true);
            setAllowGuestAccess(false);
            setGuestExpiryTime(null);
            window.localStorage.removeItem(LOCAL_STORAGE_KEYS.GUEST)

        } else {
            
            const allowGuestAccess = _.get(cmsConfiguration, 'settings.allowGuestAccessToHub', false);
            const hasGuestPass = window.localStorage.getItem(LOCAL_STORAGE_KEYS.GUEST)
            const isGuestPassValid = !!(Date.now() < hasGuestPass) && hasGuestPass && allowGuestAccess;
            
            if (isGuestPassValid ) {
                const storedQuizResults = parseJSONWithFallback(localStorage.getItem(LOCAL_STORAGE_KEYS.QUIZ));
                setUserPreferences(storedQuizResults)
                setCanRenderPersonalPage(true);
                setAllowGuestAccess(true);
                setGuestExpiryTime(hasGuestPass);
            } else {
                window.localStorage.removeItem(LOCAL_STORAGE_KEYS.GUEST)
            }
        }

    }, [isUserLoggedIn]);

    useEffect( () => {
        if (!_.isEmpty(userPreferences)) {
            if (userPreferences.atmosphere) {
                setSelectedAtmosphere(userPreferences.atmosphere);
            }
    
            if (userPreferences.myArtCollection) {
                createPersonalArtCollection(userPreferences.myArtCollection)
            }
        }
    }, [userPreferences]);

    return (
        <AppContext.Provider value={{
            allowGuestAccess,
            artCollection,
            canInitalRender,
            canRenderPersonalPage,
            customerProfile,
            cmsConfiguration,
            guestExpiryTime,
            settings,
            locale,
            userToken,
            instagramAuthCode,
            instagramAuthError,
            isUserLoggedIn,
            userPreferences,
            webglConfigurations,
            activeView,
            selectedAtmosphere,
            showLoadingScreen,
            personalArtCollection,
            quizResults,
            setQuizResults,
            setAllowGuestAccess,
            setArtCollection,
            setCmsConfiguration,
            setGuestExpiryTime,
            setSettings,
            setInstagramAuthCode,
            setInstagramAuthError,
            setIsUserLoggedIn,
            setCustomerProfile,
            setUserToken,
            setUserPreferences,
            setWebglConfigurations,
            setActiveView,
            setSelectedAtmosphere,
            setShowLoadingScreen,
            setPersonalArtCollection,
            setCanRenderPersonalPage
        }}>
            {children}
        </AppContext.Provider>
    );
};

export default AppContextProvider;